import { View } from "@common/types/views/views";
import { useEffect, useMemo, useState } from "react";
import z from "zod";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "../useWorkspaceUserCache";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "../useOrganizationUserCache";

interface UseViewStoreOptions<T extends View> {
    // View to use when there is no cached view available
    defaultView: T;
    viewSchema: z.Schema<T>;

    // Optional view that takes precedence over the cached view on mount.
    // Applied once, atomically with initialFilters, so a deep link can land on
    // a specific view without a post-mount effect racing the filters it set.
    initialView?: T;

    // Optional set of filters to apply on top of the cached
    // view when it is initially loaded from the cache.
    // These are applied once on mount and then expected to be cleared.
    // Useful for layering on url params while also maintaining
    // caching behavior.
    initialFilters?: T["filters"];

    // Like initialFilters, built from the view that opens (the initial,
    // cached or default view), e.g. to keep part of its filters.
    getInitialFilters?: (openedView: T) => T["filters"];

    // Optional set of filters that continuously override the view filters.
    // Unlike initialFilters, these are reapplied whenever they change.
    // Useful for drawers/drill-downs where filters define the context.
    parentFilters?: T["filters"];

    cacheKey?: WORKSPACE_USER_CACHE_KEYS | ORGANIZATION_USER_CACHE_KEYS;

    // Optional function to sanitize the view before caching it.
    // Useful for removing sensitive data or normalizing the view.
    // Returning undefined will unset the view from the cache.
    // Returning null will ignore setting the cache altogether.
    viewSanitizer?: (view: T | undefined) => T | undefined | null;

    // Callback invoked after initial filters have been applied
    // Used to clear URL params after consumption
    onInitialFiltersConsumed?: () => void;

    // When true, reset filters to defaultView filters on mount,
    // preserving the rest of the cached view (columns, options, etc.).
    resetFiltersOnMount?: boolean;
}

interface UseViewStoreResult<T extends View> {
    view: T;
    setView: (view: T | ((prev: T) => T)) => void;
}

export function useViewStore<T extends View>({
    defaultView,
    initialView,
    cacheKey,
    initialFilters,
    getInitialFilters,
    parentFilters,
    viewSanitizer,
    viewSchema,
    onInitialFiltersConsumed,
    resetFiltersOnMount,
}: UseViewStoreOptions<T>): UseViewStoreResult<T> {
    // Determine if the cache key is for organization or workspace
    const isOrganizationCache = cacheKey
        ? Object.values(ORGANIZATION_USER_CACHE_KEYS).includes(
              cacheKey as ORGANIZATION_USER_CACHE_KEYS,
          )
        : false;

    const isWorkspaceCache = cacheKey
        ? Object.values(WORKSPACE_USER_CACHE_KEYS).includes(
              cacheKey as WORKSPACE_USER_CACHE_KEYS,
          )
        : false;

    // Call both hooks unconditionally (hooks rules)
    const [workspaceCachedView, setWorkspaceCachedView] =
        useWorkspaceUserCache<T>(
            isWorkspaceCache
                ? (cacheKey as WORKSPACE_USER_CACHE_KEYS)
                : undefined,
            viewSchema,
        );

    const [orgCachedView, setOrgCachedView] = useOrganizationUserCache<T>(
        isOrganizationCache
            ? (cacheKey as ORGANIZATION_USER_CACHE_KEYS)
            : undefined,
        viewSchema,
    );

    // Use the appropriate cached value
    const cachedView = isOrganizationCache
        ? orgCachedView
        : workspaceCachedView;
    const setCachedView = isOrganizationCache
        ? setOrgCachedView
        : setWorkspaceCachedView;

    const [view, setView] = useState<T>(() => {
        const baseView = initialView ?? cachedView ?? defaultView;
        const filters =
            initialFilters ??
            getInitialFilters?.(baseView) ??
            parentFilters ??
            (resetFiltersOnMount ? defaultView.filters : baseView.filters);

        onInitialFiltersConsumed?.();

        return {
            ...baseView,
            filters,
        };
    });

    // Apply parent filters whenever they change
    useEffect(() => {
        if (parentFilters) {
            setView((prev) => ({
                ...prev,
                filters: parentFilters,
            }));
        }
    }, [parentFilters]);

    useEffect(() => {
        const viewToCache = viewSanitizer ? viewSanitizer(view) : view;
        if (viewToCache) {
            setCachedView(viewToCache);
        }
    }, [viewSanitizer, setCachedView, view]);

    return useMemo(
        () => ({
            view,
            setView,
        }),
        [view, setView],
    );
}
